// frontend/src/components/ReConsentPrompt.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRequiredConsents, recordConsent } from "../api/auth";

const DOC_LABELS = {
  tos: "Terms of Service",
  privacy_policy: "Privacy Policy",
  dpa: "Data Processing Addendum",
  cookie_policy: "Cookie Policy",
};
const DOC_LINKS = {
  tos: "/legal/terms",
  privacy_policy: "/legal/privacy",
  cookie_policy: "/legal/cookies",
  dpa: "/legal/dpa",
};

/**
 * ReConsentPrompt — when a required legal document has been updated to a version
 * the signed-in user hasn't accepted yet, block the app with a modal asking them
 * to re-accept. Acceptance is written to the consent ledger (admin-visible).
 * Renders nothing when everything is up to date. Mounted inside the authed shell.
 */
function ReConsentPrompt() {
  const [outstanding, setOutstanding] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getRequiredConsents()
      .then((rows) => {
        if (active) setOutstanding((rows || []).filter((r) => !r.accepted));
      })
      .catch(() => {
        /* not signed in / transient — don't block the app */
      });
    return () => {
      active = false;
    };
  }, []);

  if (outstanding.length === 0) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await Promise.all(outstanding.map((r) => recordConsent(r.document_type)));
      setOutstanding([]);
    } catch {
      // Leave the prompt up so the user can retry.
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconsent-title"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 id="reconsent-title" className="text-lg font-semibold text-gray-900 mb-2">
          We’ve updated our policies
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Please review and accept the updated{" "}
          {outstanding.length === 1 ? "document" : "documents"} below to continue
          using QRFlow.
        </p>
        <ul className="mb-5 space-y-1 text-sm">
          {outstanding.map((r) => (
            <li key={r.document_type} className="flex items-center justify-between">
              <Link
                to={DOC_LINKS[r.document_type] || "/legal"}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                {DOC_LABELS[r.document_type] || r.document_type}
              </Link>
              <span className="text-xs text-gray-400">v{r.current_version}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Saving…" : "I accept"}
        </button>
      </div>
    </div>
  );
}

export default ReConsentPrompt;
